/** @format */

import React, { useState, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Context } from "../stores/Provider";
import { PostMethod } from "../api/Api";
import { confirmPasswordValidate, mailValidate } from "../validators/publicValidator";
import { setIsLoading, passCheck } from "../function";
import { initApiModelType, userRegisterRequestType } from "../constance/typeModel";
import { initApiResponse } from "../constance/constance";

const useUserRegisterHooks = (navigateByAgent: Function) => {
  const { dispatch } = useContext(Context);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [errMsgs, setErrMsgs] = useState<{ email: string; confirmPassword: string; agentMasterId: string }>({
    email: "",
    confirmPassword: "",
    agentMasterId: "",
  });
  const [passCheckState, setPassCheckState] = useState<{ msg: string; score: number }>({ msg: "", score: 0 });
  const location = useLocation();

  // URLパラメータの取得
  const agentMasterId = useMemo(() => {
    let result: string | null = null;
    const urlParams = new URLSearchParams(location.search);
    result = urlParams.get("agent_master_id");
    return result;
  }, [location.search]);

  // URLパラメータの取得
  const plan_code = useMemo(() => {
    let result: string | null = null;
    const urlParams = new URLSearchParams(location.search);
    result = urlParams.get("plan_code");
    return result;
  }, [location.search]);

  // 代理店マスターIDにバリデーションメッセージ
  const agentMasterIdErrorMessage =
    "エラーが発生しました。\n前の画面に戻り、「お申し込み」ボタンから再度こちらの画面に入りなおしてください。";

  // ログインページへのリンク生成
  const loginLinkUrl = useMemo(() => {
    let result: string = "";
    if ((plan_code && ["ocnk"].includes(plan_code)) || (agentMasterId && ["1"].includes(agentMasterId))) {
      result = "/login?plan_code=ocnk";
    } else {
      setErrMsgs({ email: "", confirmPassword: "", agentMasterId: agentMasterIdErrorMessage });
      result = "/user_register";
    }
    return result;
  }, [agentMasterId, plan_code]);

  // API実行
  const UserRegister = async () => {
    let response: initApiModelType<undefined> = initApiResponse;
    const emailVal = mailValidate(email);
    const confirmPasswordVal = confirmPasswordValidate(password, confirmPassword);
    setErrMsgs({ email: "", confirmPassword: "", agentMasterId: "" });

    // 不正なURLパラメータの場合
    if ((plan_code && !["ocnk"].includes(plan_code)) || (agentMasterId && !["1"].includes(agentMasterId))) {
      setErrMsgs({ email: "", confirmPassword: "", agentMasterId: agentMasterIdErrorMessage });
      return response;
    } else if (!plan_code && !agentMasterId) {
      setErrMsgs({ email: "", confirmPassword: "", agentMasterId: agentMasterIdErrorMessage });
      return response;
    }

    // バリデーションの確認
    if (!emailVal.isValidate || !confirmPasswordVal.isValidate) {
      setErrMsgs({
        email: emailVal.message,
        confirmPassword: confirmPasswordVal.message,
        agentMasterId: "",
      });
      return response;
    }

    const data = {
      email: email,
      password: password,
      agent_master_id: agentMasterId,
    };

    setIsLoading(true, dispatch);
    response = await PostMethod<userRegisterRequestType, undefined>(data, "/user_register");

    return response;
  };

  const normal = () => {
    return navigateByAgent("/completed_register");
  };

  const abnormal = (response: initApiModelType<undefined>) => {
    // 登録済みユーザーの場合
    if (response.code === 409) {
      setErrMsgs({ email: "", confirmPassword: "", agentMasterId: response.message });
    }
    // 以外の400エラー
    else {
      setErrMsgs({ email: "", confirmPassword: "", agentMasterId: agentMasterIdErrorMessage });
    }
    return navigateByAgent(`/user_register?agent_master_id=${agentMasterId}`);
  };

  const setEmailHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    return setEmail(e.target.value);
  };

  const setPasswordHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const usedByEmailStr = email !== "" ? email.split("@")[0] : "";
    setPassCheckState(passCheck(e.target.value, [email, usedByEmailStr]));
    return setPassword(e.target.value);
  };

  const setConfirmPasswordHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    return setConfirmPassword(e.target.value);
  };

  return {
    UserRegister,
    loginLinkUrl,
    normal,
    abnormal,
    setEmailHandler,
    setPasswordHandler,
    setConfirmPasswordHandler,
    errMsgs,
    passCheckState,
  };
};

export default useUserRegisterHooks;
